import React, { useState } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCalendarAlt, 
  faClock, 
  faUser, 
  faEnvelope, 
  faPhone,
  faCheckCircle,
  faRoute
} from '@fortawesome/free-solid-svg-icons';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import axiosInstance from '../../helpers/api';
import toast from 'react-hot-toast';

interface TourSchedulingModalProps {
  show: boolean;
  onHide: () => void;
  tourProperties: any[];
  userInfo?: {
    name?: string;
    email?: string;
    phone?: string;
  };
}

const TourSchedulingModal: React.FC<TourSchedulingModalProps> = ({
  show,
  onHide,
  tourProperties,
  userInfo
}) => {
  const [formData, setFormData] = useState({
    customerName: '', // Always start empty - user must fill in every time
    customerEmail: '', // Always start empty - user must fill in every time
    customerPhone: '', // Always start empty - user must fill in every time
    preferredDate: new Date(),
    preferredTimeSlot: 'morning',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const timeSlots = [
    { value: 'morning', label: 'Morning (9am - 12pm)' },
    { value: 'afternoon', label: 'Afternoon (12pm - 5pm)' },
    { value: 'evening', label: 'Evening (5pm - 7pm)' },
    { value: 'flexible', label: 'I\'m Flexible' }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.customerName || !formData.customerEmail) {
      toast.error('Please provide your name and email');
      return;
    }

    setLoading(true);
    
    try {
      const response = await axiosInstance.post('/property-tours/create', {
        properties: tourProperties.map(p => ({
          mlsNumber: p.mlsNumber,
          address: `${p.address.street}, ${p.address.city}, ${p.address.state} ${p.address.zip}`,
          price: p.price,
          bedrooms: p.bedrooms,
          bathrooms: p.bathrooms,
          sqft: p.sqft,
          images: p.images
        })),
        ...formData
      });

      if (response.data.success) {
        setSuccess(true);
        
        // Clear tour from localStorage
        localStorage.removeItem('tourProperties');
        window.dispatchEvent(new Event('tourUpdated'));
        
        setTimeout(() => {
          onHide();
          toast.success('Tour request submitted! An agent will contact you soon.');
        }, 2000);
      }
    } catch (error) {
      console.error('Error submitting tour request:', error);
      toast.error('Failed to submit tour request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Modal show={show} onHide={onHide} centered>
        <Modal.Body className="text-center py-5">
          <FontAwesomeIcon icon={faCheckCircle} size="4x" className="text-success mb-4" />
          <h4>Tour Request Submitted!</h4>
          <p>We'll contact you within 24 hours to confirm your tour schedule.</p>
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faRoute} className="me-2" />
          Schedule Your Property Tour
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        <Alert variant="info" className="mb-4">
          <strong>You've selected {tourProperties.length} {tourProperties.length === 1 ? 'property' : 'properties'} to tour.</strong>
          <br />
          Tell us when you're available and we'll coordinate everything for you.
        </Alert>

        <Form onSubmit={handleSubmit}>
          <h5 className="mb-3">Your Information</h5>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  <FontAwesomeIcon icon={faUser} className="me-2" />
                  Your Name
                </Form.Label>
                <Form.Control
                  type="text"
                  value={formData.customerName}
                  onChange={(e) => setFormData({...formData, customerName: e.target.value})}
                  required
                  placeholder="John Doe"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                  Email Address
                </Form.Label>
                <Form.Control
                  type="email"
                  value={formData.customerEmail}
                  onChange={(e) => setFormData({...formData, customerEmail: e.target.value})}
                  required
                  placeholder="john@example.com"
                />
              </Form.Group>
            </Col>
          </Row>
          
          <Row className="mb-4">
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  <FontAwesomeIcon icon={faPhone} className="me-2" />
                  Phone Number (Optional)
                </Form.Label>
                <Form.Control
                  type="tel"
                  value={formData.customerPhone}
                  onChange={(e) => setFormData({...formData, customerPhone: e.target.value})}
                  placeholder="(555) 123-4567"
                />
              </Form.Group>
            </Col>
          </Row>

          <h5 className="mb-3">When would you like to tour?</h5>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                  Preferred Date
                </Form.Label>
                <DatePicker
                  selected={formData.preferredDate}
                  onChange={(date: Date | null) => setFormData({...formData, preferredDate: date || new Date()})}
                  minDate={new Date()}
                  className="form-control"
                  dateFormat="MMMM d, yyyy"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  <FontAwesomeIcon icon={faClock} className="me-2" />
                  Preferred Time
                </Form.Label>
                <Form.Select
                  value={formData.preferredTimeSlot}
                  onChange={(e) => setFormData({...formData, preferredTimeSlot: e.target.value})}
                >
                  {timeSlots.map(slot => (
                    <option key={slot.value} value={slot.value}>
                      {slot.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-4">
            <Form.Label>Additional Notes (Optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Any specific requests or additional dates that work for you..."
            />
          </Form.Group>

          <div className="selected-properties mb-4">
            <h6>Properties to Tour:</h6>
            <div className="property-list" style={{ maxHeight: '150px', overflowY: 'auto' }}>
              {tourProperties.map((property, index) => (
                <div key={property.mlsNumber} className="border-bottom py-2">
                  <small>
                    {index + 1}. {property.address.street}, {property.address.city} - 
                    ${property.price.toLocaleString()}
                  </small>
                </div>
              ))}
            </div>
          </div>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Submitting...' : 'Submit Tour Request'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TourSchedulingModal;